import { CenteredScrollPage } from '@/components/shared/CenteredScrollPage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InstallPWAButton } from '@/components/ui/install-pwa-button';
import { useLogin } from '@/hooks/useAuthQueries';
import { zodResolver } from '@hookform/resolvers/zod';
import { MfaMethod } from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { LoginMfa } from './LoginMfa';
import { LoginMfaEmailCode } from './LoginMfaEmailCode';
import { LoginMfaMethodSelector } from './LoginMfaMethodSelector';
import { LoginMfaPasskey } from './LoginMfaPasskey';
import { LOGIN_NOTICE_PARAM, resolveLoginNotice } from './login-notices';
import { Notice } from '@/components/ui/notice';

type LoginStep = 'credentials' | 'mfa' | 'emailCode' | 'passkey' | 'passwordReset';

// Form validation schema - matches backend validation
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethods, setMfaMethods] = useState<MfaMethod[]>([]);
  const [error, setError] = useState('');
  
  const loginMutation = useLogin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notice = resolveLoginNotice(searchParams.get(LOGIN_NOTICE_PARAM));

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  const handleMfaSuccess = async () => {
    // Navigate to explore - tokens are already stored by the login hook
    navigate('/explore');
  };

  const handleMfaError = (errorMessage: string) => {
    setError(errorMessage);
    toast.error(errorMessage);
  };

  const handleMfaMethodSelect = (method: MfaMethod) => {
    setError('');
    if (method === 'EMAIL') {
      setStep('emailCode');
    } else if (method === 'FIDO2') {
      setStep('passkey');
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setError('');
    setMfaToken('');
    setMfaMethods([]);
  };

  const handleBackToMfa = () => {
    setStep('mfa');
    setError('');
  };

  return (
    <CenteredScrollPage>
      {!loginMutation.data ? (
        <Card className="w-full max-w-sm">
          <CardHeader className="relative">
            <div className="absolute top-4 right-4">
              <InstallPWAButton />
            </div>
            <CardTitle>Login</CardTitle>
            <CardDescription>Enter your credentials to sign in</CardDescription>
          </CardHeader>
          <CardContent>
            {notice && (
              <Notice tone="warning" data-testid="login-notice" className="mb-4 rounded p-3 text-base">
                <p className="text-sm font-medium">{notice.title}</p>
                <p className="mt-1 text-sm">{notice.body}</p>
              </Notice>
            )}

            <form onSubmit={handleSubmit(data => loginMutation.mutate(data))} className="space-y-4">
              <div>
                <Input 
                  data-testid="login-email" 
                  type="email" 
                  placeholder="Email" 
                  {...register('email')}
                  disabled={loginMutation.isPending}
                />
                {errors.email && (
                  <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
              
              <div>
                <Input
                  data-testid="login-password"
                  type="password"
                  placeholder="Password"
                  {...register('password')}
                  disabled={loginMutation.isPending}
                />
                {errors.password && (
                  <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
                )}
              </div>
              
              <Button 
                data-testid="login-button" 
                type="submit" 
                className="w-full"
                disabled={loginMutation.isPending || isSubmitting}
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Button
                variant="link"
                onClick={() => navigate('/reset-password')}
                disabled={loginMutation.isPending}
                className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
              >
                Forgot your password?
              </Button>
            </div>

            <div className="mt-2 text-center">
              <Button
                data-testid="login-signup-link"
                variant="link"
                onClick={() => navigate('/signup')}
                disabled={loginMutation.isPending}
                className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
              >
                Don't have an account? Sign up
              </Button>
            </div>

            {loginMutation.isError && (
              <p data-testid="login-error" className="text-destructive mt-4 text-sm">
                {loginMutation.error?.message || 'Login failed. Please try again.'}
              </p>
            )}
          </CardContent>
        </Card>
      ) :
      'mfaRequired' in loginMutation.data ? (
        <LoginMfa
          mfaToken={loginMutation.data.mfaToken}
          mfaMethods={loginMutation.data.mfaMethods}
          onBack={handleBackToCredentials}
        />
      ) : (
        <Navigate to="/explore" replace />
      )}

      {step === 'mfa' && (
        <LoginMfaMethodSelector
          mfaMethods={mfaMethods}
          onMethodSelect={handleMfaMethodSelect}
          onBack={handleBackToCredentials}
        />
      )}

      {step === 'emailCode' && (
        <LoginMfaEmailCode
          mfaToken={mfaToken}
          onSuccess={handleMfaSuccess}
          onError={handleMfaError}
          onBack={handleBackToMfa}
        />
      )}

      {step === 'passkey' && (
        <LoginMfaPasskey
          mfaToken={mfaToken}
          onSuccess={handleMfaSuccess}
          onError={handleMfaError}
          onBack={handleBackToMfa}
          autoTrigger={true}
        />
      )}


      {error && step !== 'credentials' && (
        <Notice tone="destructive" className="fixed bottom-4 right-4 max-w-sm rounded px-4 py-3 text-base shadow-lg">
          <p className="text-sm">{error}</p>
        </Notice>
      )}
    </CenteredScrollPage>
  );
}
